import React, { memo } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { IHouse, IMatch } from "../../../interfaces/interfaces";
import { RoomPhotosCarousel } from "../RoomPhotosCarousel";
import { HouseDoorIcon, RoomStatusBoxRoot, BottomBox } from "./styles";
import {
  Check2Circle as VerifiedIcon,
  Heart as HeartIcon,
  HeartFill as HeartFillIcon
} from "@styled-icons/bootstrap";
import {
  CloseButton,
  Dates,
  HostAvatar,
  HostContainer,
  HostLabel,
  HostName,
  HostNameContainer,
  LikeButtonRoot,
  PhotoPlaceholder,
  RoomInfo,
  RoomName,
  RoomPhotosCarouselContainer,
  RoomPrice,
  RoomSize,
  RoomSizeMeasureUnits,
  RoomType,
  Root,
  VerifiedBadgeRoot
} from "./styles";
import { useTranslation } from "react-i18next";
import { Popover, Skeleton } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { useCurrentUser, useIsHost } from "../../../utils/helpers";
import { useGetUserShortDescription } from "../../../API/queries/userApiQueries";
import { LoadingIndicator } from "../../LoadingIndicator";
import { useChangeLiked } from "../../../API/queries/houseApiQueries";
import {
  useGetStudentMatches,
  useStudentAcceptInvite
} from "../../../API/queries/studentApiQueries";
import { MatchStatusEnum, ROOM_INFO_ROUTE } from "../../../utils/constants";
import { calculateHousePrice } from "../../Chat/utils";
import { useNotifier } from "../../../context/ContextNotifier";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { useIsMobile } from "../../../utils/hooks/useWindowSize";
import { getHouseName } from "../../../utils/house";
import { Placeholder } from "../../Icons/Generic";

export interface IRoomCard {
  house: IHouse;
  onClose?: () => void;
  onHover?: (e: IHouse) => void;
  onLikedChanged: (e: IHouse) => void;
  className?: string;
}

export const RoomStatusBox: React.FC<{
  children: React.ReactNode;
  tooltipText: React.ReactNode;
  color?: "default" | "orange" | "blue";
  size?: SizeType;
  onClick?: () => void;
  isLoading?: boolean;
}> = ({
  tooltipText,
  children,
  color = "default",
  onClick,
  isLoading,
  size = "middle"
}) => {
  return (
    <Popover content={tooltipText} placement="bottom">
      <RoomStatusBoxRoot
        color={color}
        onClick={onClick}
        loading={isLoading}
        size={size}
      >
        {children} <InfoCircleOutlined />
      </RoomStatusBoxRoot>
    </Popover>
  );
};

export const RoomStatus: React.FC<{ match?: IMatch }> = ({ match }) => {
  const { mutate: acceptInvite, isLoading: isLoadingAcceptInvite } =
    useStudentAcceptInvite();
  const notifier = useNotifier();
  const { t } = useTranslation("hostrooms");
  const isMobile = useIsMobile();

  const handleAccept = () => {
    if (!match) {
      return;
    }
    acceptInvite(
      {
        host_id: match.host_id,
        house_id: match.house_id,
        student_id: match.student_id
      },
      {
        onSuccess: () => {
          notifier.success({
            message: "Invite accepted!"
          });
        }
      }
    );
  };
  // Don't render component if on a mobile device
  if (isMobile) {
    return null;
  }

  if (!match) {
    return (
      <RoomStatusBox tooltipText={t("roomCard.newRoom")}>
        {t("roomCard.newListing")}
      </RoomStatusBox>
    );
  }
  // Chat request sent
  if (match.match_status === MatchStatusEnum.LIKED) {
    return (
      <RoomStatusBox tooltipText={t("roomCard.awaitingConfirmation")}>
        {t("roomCard.chatRequestSent")}
      </RoomStatusBox>
    );
  }
  if (match.match_status === MatchStatusEnum.CHATTED) {
    return (
      <RoomStatusBox tooltipText="Chat request accepted. Now you can discuss dates with host">
        {t("roomCard.chatRequestAccepted")}
      </RoomStatusBox>
    );
  }
  if (match.match_status === MatchStatusEnum.INVITED) {
    return (
      <RoomStatusBox
        tooltipText={t("roomCard.acceptInviteToolTip")}
        color="orange"
        onClick={handleAccept}
        isLoading={isLoadingAcceptInvite}
      >
        {t("roomCard.acceptInvite")}
      </RoomStatusBox>
    );
  }
  if (
    match.match_status === MatchStatusEnum.ACCEPTED ||
    match.match_status === MatchStatusEnum.PENDING ||
    match.match_status === MatchStatusEnum.ACTIVE
  ) {
    return (
      <RoomStatusBox
        tooltipText={t("roomCard.yourNewRoomToolTip")}
        color="blue"
      >
        {t("roomCard.yourNewRoom")}
      </RoomStatusBox>
    );
  }

  if (match.match_status === MatchStatusEnum.ARCHIVED) {
    return (
      <RoomStatusBox tooltipText="Archived">
        {t("roomCard.archived")}
      </RoomStatusBox>
    );
  }
  return (
    <RoomStatusBox tooltipText={"Unknown status"}>
      Status code: {match.match_status}
    </RoomStatusBox>
  );
};

interface ILikeButton extends React.HTMLProps<HTMLButtonElement> {
  liked?: boolean;
  isLoading?: boolean;
}

const LikeButton: React.FC<ILikeButton> = ({ liked, isLoading, onClick }) => {
  return (
    <LikeButtonRoot onClick={onClick}>
      {isLoading ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <LoadingIndicator />
        </div>
      ) : (
        <>{liked ? <HeartFillIcon /> : <HeartIcon />}</>
      )}
    </LikeButtonRoot>
  );
};

interface IProps {
  t: Function;
}

const VerifiedBadge: React.FC<IProps> = ({ t }) => {
  return (
    <VerifiedBadgeRoot>
      <VerifiedIcon /> <span>{t("roomCard.verified")}</span>
    </VerifiedBadgeRoot>
  );
};

export const RoomCard: React.FC<IRoomCard> = memo(
  ({ house, onClose, onHover, onLikedChanged, className }) => {
    const { t, i18n } = useTranslation("hostrooms");
    const currentUser = useCurrentUser();
    const isHost = useIsHost();
    const { data: studentMatches } = useGetStudentMatches(
      currentUser?.user_id,
      !isHost
    );

    const changeLiked = useChangeLiked();

    const { data: user, isLoading: isLoadingUser } = useGetUserShortDescription(
      house.user_id
    );

    const photos = house?.photos ?? [];

    const houseMatch = studentMatches?.find(
      (m) => m.house_id === house.house_id
    );

    const roomName = user
      ? getHouseName(house.kind!, {
          first_name: user.first_name!,
          last_name: user.last_name!
        })
      : t("roomCard.noName");

    const navigate = useNavigate();

    const handleCardElementClick = (
      event: React.MouseEvent<HTMLDivElement>
    ) => {
      event.stopPropagation();

      const url =
        generatePath(ROOM_INFO_ROUTE, { lng: i18n.language }) +
        "/" +
        house.house_id;
      navigate(url);
    };

    return (
      <Root
        className={className}
        onClick={handleCardElementClick}
        onMouseEnter={() => {
          onHover?.(house);
        }}
      >
        <CloseButton
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
        />
        <HostContainer>
          {isLoadingUser ? (
            <>
              <Skeleton.Avatar
                active={true}
                size={"default"}
                shape={"circle"}
              />
              <Skeleton.Input
                active={true}
                size={"default"}
                style={{ marginRight: "20px" }}
                className="SkeletonInput"
              />
            </>
          ) : (
            <>
              <HostAvatar src={user?.thumbnail_url} />
              <HostNameContainer>
                <HostLabel>{t("roomCard.host")}</HostLabel>
                <HostName>
                  {user ? `${user?.first_name} ${user?.last_name}` : ""}
                </HostName>
              </HostNameContainer>
            </>
          )}
        </HostContainer>
        <RoomPhotosCarouselContainer>
          {photos.length > 0 ? (
            <RoomPhotosCarousel
              images={photos.map((p) => {
                // @ts-ignore
                return p.url;
              })}
            />
          ) : (
            <PhotoPlaceholder>{Placeholder}</PhotoPlaceholder>
          )}
          <LikeButton
            liked={house.is_favorited}
            isLoading={changeLiked.isLoading}
            onClick={(e) => {
              e.stopPropagation();
              if (changeLiked.isLoading) return;

              changeLiked.mutate(
                {
                  isLiked: Boolean(house.is_favorited),
                  houseId: house.house_id!,
                  studentId: currentUser?.user_id!,
                  hostId: house.user_id!
                },
                {
                  onSuccess: () => {
                    onLikedChanged({
                      ...house,
                      is_favorited: !Boolean(house.is_favorited)
                    });
                  }
                }
              );
            }}
          />
          {house.verification_status ? <VerifiedBadge t={t} /> : null}
        </RoomPhotosCarouselContainer>
        <Dates>
          {house.available_from} - {house.available_to}
        </Dates>
        <div>
          <RoomName title={roomName}>{roomName}</RoomName>
          {/* <RoomLocation title={house.street}>{house.street}</RoomLocation> */}
          <RoomInfo>
            <RoomPrice>
              €{calculateHousePrice(house)}
              -p.m.
            </RoomPrice>
            <RoomSize>
              {house.bedroom_area}{" "}
              <RoomSizeMeasureUnits>m2</RoomSizeMeasureUnits>
            </RoomSize>
            <RoomType>
              <HouseDoorIcon />
              {t(`roomsDetails.typeRoom.items.${house.kind}.title`)}
            </RoomType>
          </RoomInfo>
        </div>

        <BottomBox
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <RoomStatus match={houseMatch} />
        </BottomBox>
      </Root>
    );
  }
);
